import { ENTIDADE_TIPOS, REGULACAO_STATUS, EVENTO_NATUREZAS, CANAIS_ENTREGA, UFS } from "./pre-analise/field-options";
import { z } from "zod";
import {
  insertEmpresaSchema,
  insertUserProfileSchema,
  insertVeiculoSchema,
  insertInviteCodeSchema,
  insertUserInviteSchema,
  insertRemontaLaudoSchema,
  insertRemontaCategoriaEstrutural,
  insertRemontaElementoEstrutural,
  insertRemontaItemLaudo,
  insertRemontaSinistroLocalizacaoSchema,
  insertRemontaOrcamentoItemSchema,
  empresas,
  userProfiles,
  veiculos,
  inviteCodes,
  userInvites,
  remontaLaudos,
  remontaCategoriasEstruturais,
  remontaElementosEstruturais,
  remontaItensLaudo,
  remontaSinistroLocalizacoes,
  remontaApks,
  remontaSolicitantePermissoes,
  remontaDossiesSinistro,
  remontaOrdensServico,
  remontaDossieArquivos,
  remontaTiposFoto,
  remontaOrcamentosItem,
  remontaOcorrencias,
} from "./schema";
import { ACTIVE_SERVICE_TYPES } from "./pre-analise/types";

const userCpf = z.string().transform(value => value.replace(/\D/g, ""))
  .pipe(z.string().regex(/^\d{11}$/, "Informe o CPF do usuário com 11 dígitos"));
const companyCnpj = z.string().transform(value => value.replace(/\D/g, ""))
  .pipe(z.string().regex(/^\d{14}$/, "Informe o CNPJ da empresa com 14 dígitos"));
const companyFields = insertEmpresaSchema.omit({ cpf: true, email: true });

export const errorSchemas = {
  validation: z.object({
    message: z.string(),
    field: z.string().optional(),
  }),
  notFound: z.object({
    message: z.string(),
  }),
  internal: z.object({
    message: z.string(),
  }),
};

const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    if (year < 1 || month < 1 || month > 12 || day < 1) return false;
    return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
  }, "Data invalida");
const decimalSchema = z.string().regex(/^\d+(?:\.\d{1,2})?$/);
// numeric(12,2): 10 digitos inteiros + 2 decimais. Preserva a exigencia de
// positividade que os campos monetarios tinham com z.coerce.number().positive().
const moneySchema = decimalSchema
  .refine((v) => Number(v) > 0, "Valor deve ser maior que zero")
  .refine((v) => Number(v) <= 9_999_999_999.99, "Valor excede o limite permitido");
// Campo opcional de texto no form que, deixado em branco, envia "" em vez de
// omitir a chave — "" nunca deve chegar a um INSERT numeric/date/uuid.
const emptyStringToUndefined = z
  .string()
  .optional()
  .transform((v) => (v === "" ? undefined : v));
const anexoSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
]);
const dossieStatusSchema = z.enum([
  "rascunho",
  "solicitado",
  "pendencia",
  "agendado",
  "em_inspecao",
  "pronto_emissao",
  "emitido",
  "cancelado",
]);
const dossieArquivoTipoSchema = z.enum([
  "foto_triagem",
  "bat",
  "relatorio_avarias",
  "notificacao",
  "documento_outro",
]);
export const remontaServiceTypeSchema = z.enum(ACTIVE_SERVICE_TYPES);

const intakeText = z.string().trim().min(1).max(10_000).optional();
const intakeMoney = decimalSchema.refine((value) => Number(value) <= 9_999_999_999.99, "Valor excede o limite permitido").optional();
/** F01 "Dados do atendimento técnico": vão para a primeira Ordem, nunca para o snapshot do Dossiê. */
export const firstOrderDetailsSchema = z.object({
  numeroOrdemExterna: z.string().trim().min(1).max(200).optional(),
  reguladorExterno: z.string().trim().min(1).max(200).optional(),
  vistoriaPrevistaEm: isoDateSchema.optional(),
  prazoEntregaEm: isoDateSchema.optional(),
}).strict();
export type FirstOrderDetails = z.infer<typeof firstOrderDetailsSchema>;

const intakeCommonSchema = z.object({
  solicitanteId: z.string().uuid().optional(),
  dataSinistro: isoDateSchema,
  numeroBat: intakeText,
  valorFipe: intakeMoney,
  mesReferenciaFipe: isoDateSchema.optional(),
  clienteEntidade: intakeText, entidadeTipo: z.enum(ENTIDADE_TIPOS).optional(), entidadeCnpj: intakeText,
  unidadeFilial: intakeText, profissionalSolicitante: intakeText, solicitanteSetor: intakeText,
  solicitanteEmail: z.string().trim().email().max(254).optional(), solicitanteTelefone: intakeText,
  solicitadoEm: isoDateSchema.optional(), prazoSolicitado: isoDateSchema.optional(),
  numeroSinistro: intakeText, numeroApolice: intakeText, seguradoNome: intakeText, seguradoDocumento: intakeText,
  vigenciaInicio: isoDateSchema.optional(), vigenciaFim: isoDateSchema.optional(),
  coberturaAcionada: intakeText, franquiaParticipacao: intakeMoney, regulacaoStatus: z.enum(REGULACAO_STATUS).optional(), valorReclamado: intakeMoney,
  eventoHorario: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/).optional(),
  eventoMunicipio: intakeText, eventoUf: z.enum(UFS).optional(), eventoEndereco: intakeText,
  eventoLocalizacao: intakeText, eventoOrgaoRegistro: intakeText,
  eventoNaturezas: z.array(z.enum(EVENTO_NATUREZAS)).max(6).optional(), descricaoEvento: intakeText,
  veiculosEnvolvidos: intakeText, bensTerceirosEnvolvidos: intakeText,
  contatoAcessoNome: intakeText, contatoAcessoTelefone: intakeText, canalEntrega: z.enum(CANAIS_ENTREGA).optional(),
  documentosIniciaisReferencia: intakeText, responsavelInformacoesNome: intakeText, responsavelInformacoesCargo: intakeText,
  autorizadoEnvio: z.boolean().optional(),
  primeiroServico: remontaServiceTypeSchema.optional(),
  primeiraOrdem: firstOrderDetailsSchema.optional(),
  modo: z.enum(["rascunho", "enviar"]).optional(),
});

const formalizationShape = intakeCommonSchema.omit({ solicitanteId: true, primeiroServico: true, primeiraOrdem: true, modo: true }).shape;
export const dossierFormalizationInput = z.object({
  ...Object.fromEntries(Object.entries(formalizationShape).map(([key, field]) => [key, key === "dataSinistro" ? field.optional() : field.nullable().optional()])) as { [Key in keyof typeof formalizationShape]: Key extends "dataSinistro" ? z.ZodOptional<(typeof formalizationShape)[Key]> : z.ZodOptional<z.ZodNullable<(typeof formalizationShape)[Key]>> },
  versao: z.number().int().nonnegative(),
}).strict();

// Payloads antigos inferem veiculo; novos consumidores devem explicitar objetoTipo.
export const dossierIntakeSchema = z.preprocess((value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const input = value as Record<string, unknown>;
  return {
    ...input,
    objetoTipo: input.objetoTipo === undefined ? (input.primeiroServico === "pericia_bens_imoveis" ? "imovel" : input.veiculoId ? "veiculo" : "outro") : input.objetoTipo,
    modo: input.modo === undefined ? (input.primeiroServico ? "enviar" : "rascunho") : input.modo,
  };
}, z.discriminatedUnion("objetoTipo", [
  intakeCommonSchema.extend({
    objetoTipo: z.literal("veiculo"),
    veiculoId: z.string().uuid(),
    classificacaoOriginal: z.enum(["pequena_monta", "media_monta", "grande_monta"]),
    ufRegistro: z.enum(UFS),
  }),
  intakeCommonSchema.extend({ objetoTipo: z.literal("imovel"), veiculoId: z.never().optional(), classificacaoOriginal: z.never().optional(), ufRegistro: z.never().optional() }),
  intakeCommonSchema.extend({ objetoTipo: z.literal("outro"), veiculoId: z.never().optional(), classificacaoOriginal: z.never().optional(), ufRegistro: z.never().optional() }),
]).superRefine((input, context) => {
  if ((input.valorFipe === undefined) !== (input.mesReferenciaFipe === undefined)) {
    context.addIssue({ code: "custom", message: "Valor FIPE e mes de referencia devem ser informados juntos", path: ["valorFipe"] });
  }
  if (input.primeiroServico === "reclassificacao_monta" && input.objetoTipo !== "veiculo") {
    context.addIssue({ code: "custom", message: "Reclassificacao exige dados veiculares", path: ["primeiroServico"] });
  }
  if (input.vigenciaInicio && input.vigenciaFim && input.vigenciaInicio > input.vigenciaFim) {
    context.addIssue({ code: "custom", message: "Fim da vigencia anterior ao inicio", path: ["vigenciaFim"] });
  }
}));

export type DossierIntakeInput = z.infer<typeof dossierIntakeSchema>;

export const api = {
  auth: {
    me: {
      method: "GET" as const,
      path: "/api/auth/me" as const,
      responses: {
        200: z.custom<
          typeof userProfiles.$inferSelect & {
            empresa: typeof empresas.$inferSelect;
          }
        >(),
        401: errorSchemas.notFound,
      },
    },
    login: {
      method: "POST" as const,
      path: "/api/auth/login" as const,
      input: z.object({
        email: z.string().email(),
        password: z.string().min(6),
      }),
      responses: {
        200: z.object({
          user: z.custom<typeof userProfiles.$inferSelect>(),
          session: z.any(),
        }),
        401: errorSchemas.notFound,
      },
    },
    register: {
      method: "POST" as const,
      path: "/api/auth/register" as const,
      input: z.object({
        email: z.string().email(),
        password: z.string().min(6),
        nome: z.string().min(2),
        cpf: userCpf,
        codigoConvite: z.string(),
        empresa: z.object({
          razaoSocial: z.string(),
          nomeFantasia: z.string().optional(),
          cnpj: companyCnpj,
          uf: z.string().length(2),
          telefone: z.string().optional(),
          whatsapp: z.string().optional(),
          logradouro: z.string().optional(),
          numero: z.string().optional(),
          complemento: z.string().optional(),
          bairro: z.string().optional(),
          cidade: z.string().min(1),
          cep: z.string().optional(),
          tagEmpresa: z.string().optional(),
          art: z.string().optional(),
          vencimentoArt: z.string().optional(),
        }),
      }),
      responses: {
        201: z.object({
          user: z.custom<typeof userProfiles.$inferSelect>(),
          empresa: z.custom<typeof empresas.$inferSelect>(),
        }),
        400: errorSchemas.validation,
      },
    },
    logout: {
      method: "POST" as const,
      path: "/api/auth/logout" as const,
      responses: {
        200: z.object({ message: z.string() }),
      },
    },
    registerUser: {
      method: "POST" as const,
      path: "/api/auth/register-user" as const,
      input: z.object({
        nome: z.string().min(2),
        email: z.string().email(),
        password: z.string().min(6),
        codigoConvite: z.string().min(1),
        crea: z.string().optional(),
        cpf: userCpf,
      }),
      responses: {
        201: z.custom<typeof userProfiles.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
  },

  empresas: {
    list: {
      method: "GET" as const,
      path: "/api/empresas" as const,
      responses: {
        200: z.array(z.custom<typeof empresas.$inferSelect>()),
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/empresas/:id" as const,
      responses: {
        200: z.custom<typeof empresas.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/empresas/:id" as const,
      input: companyFields.extend({ cnpj: companyCnpj }).partial(),
      responses: {
        200: z.custom<typeof empresas.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/empresas" as const,
      input: companyFields.extend({ cnpj: companyCnpj }),
      responses: {
        201: z.custom<typeof empresas.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    parceiros: {
      method: "GET" as const,
      path: "/api/empresas/:id/parceiros" as const,
      responses: {
        200: z.array(z.custom<typeof empresas.$inferSelect>()),
      },
    },
    createBatch: {
      method: "POST" as const,
      path: "/api/empresas/batch" as const,
      input: z.object({
        parceiros: z
          .array(
            z.object({
              razaoSocial: z.string().min(1),
              nomeFantasia: z.string().optional(),
              cnpj: companyCnpj,
              uf: z.string().length(2),
              cidade: z.string().min(1),
              telefone: z.string().optional(),
              idProprio: z.number().int().optional(),
            }),
          )
          .min(1),
      }),
      responses: {
        200: z.object({
          criados: z.number(),
          ignorados: z.number(),
          erros: z.array(z.object({ linha: z.number(), razao: z.string() })),
        }),
      },
    },
  },

  usuarios: {
    list: {
      method: "GET" as const,
      path: "/api/usuarios" as const,
      responses: {
        200: z.array(z.custom<typeof userProfiles.$inferSelect>()),
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/usuarios/:id" as const,
      responses: {
        200: z.custom<typeof userProfiles.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/usuarios" as const,
      input: z.object({
        email: z.string().email("E-mail inválido"),
        password: z.string().min(6, "Senha deve ter ao menos 6 caracteres"),
        nome: z.string().min(2, "Nome deve ter ao menos 2 caracteres"),
        empresaId: z.string().uuid("Empresa inválida"),
        roles: z.array(z.string()).min(1, "Selecione ao menos um perfil"),
        telefone: z.string().optional(),
        whatsapp: z.string().optional(),
        cpf: userCpf,
        crea: z.string().optional(),
        art: z.string().optional(),
        logradouro: z.string().optional(),
        numero: z.string().optional(),
        complemento: z.string().optional(),
        bairro: z.string().optional(),
        cidade: z.string().optional(),
        estado: z.string().length(2).optional(),
        cep: z.string().optional(),
      }).superRefine((data, ctx) => {
        if (data.roles.includes("rt") && !data.crea?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "CREA é obrigatório para Responsável Técnico",
            path: ["crea"],
          });
        }
      }),
      responses: {
        201: z.custom<typeof userProfiles.$inferSelect>(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/usuarios/:id" as const,
      input: insertUserProfileSchema.partial(),
      responses: {
        200: z.custom<typeof userProfiles.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    changePassword: {
      method: "POST" as const,
      path: "/api/usuarios/:id/change-password" as const,
      input: z
        .object({
          senhaAtual: z.string().min(6, "Senha atual obrigatória"),
          novaSenha: z
            .string()
            .min(6, "Nova senha deve ter ao menos 6 caracteres"),
          confirmarSenha: z.string(),
        })
        .refine((data) => data.novaSenha === data.confirmarSenha, {
          message: "Senhas não correspondem",
          path: ["confirmarSenha"],
        }),
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
        401: errorSchemas.notFound,
        403: errorSchemas.validation,
      },
    },
    resetPassword: {
      method: "POST" as const,
      path: "/api/usuarios/:id/reset-password" as const,
      input: z
        .object({
          novaSenha: z
            .string()
            .min(6, "Nova senha deve ter ao menos 6 caracteres"),
          confirmarSenha: z.string(),
        })
        .refine((data) => data.novaSenha === data.confirmarSenha, {
          message: "Senhas não correspondem",
          path: ["confirmarSenha"],
        }),
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
        401: errorSchemas.notFound,
        403: errorSchemas.validation,
      },
    },
  },

  veiculos: {
    list: {
      method: "GET" as const,
      path: "/api/veiculos" as const,
      responses: {
        200: z.array(z.custom<typeof veiculos.$inferSelect>()),
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/veiculos/:id" as const,
      responses: {
        200: z.custom<typeof veiculos.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/veiculos" as const,
      input: insertVeiculoSchema,
      responses: {
        201: z.custom<typeof veiculos.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/veiculos/:id" as const,
      input: insertVeiculoSchema.partial(),
      responses: {
        200: z.custom<typeof veiculos.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/veiculos/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    search: {
      method: "GET" as const,
      path: "/api/veiculos/search" as const,
      responses: {
        200: z.array(z.custom<typeof veiculos.$inferSelect>()),
      },
    },
    parceiros: {
      method: "GET" as const,
      path: "/api/veiculos/parceiros" as const,
      responses: {
        200: z.array(z.any()),
      },
    },
    createBatch: {
      method: "POST" as const,
      path: "/api/veiculos/batch" as const,
      input: z.object({ veiculos: z.array(z.unknown()).min(1, "Lista de veículos inválida."), parceiroId: z.string().uuid().optional() }),
      responses: {
        400: errorSchemas.validation,
        200: z.object({
          criados: z.number(),
          ignorados: z.number(),
          erros: z.array(z.object({ linha: z.number(), razao: z.string() })),
        }),
      },
    },
  },

  remontaSinistroLocalizacao: {
    get: {
      method: "GET" as const,
      path: "/api/remonta/veiculos/:veiculoId/sinistro-localizacao" as const,
      responses: {
        // Resposta enriquecida (localizacao + desmontes[]), não mapeia a um
        // único $inferSelect — mesmo padrão pragmático já usado em
        // veiculos.parceiros (z.array(z.any())) neste arquivo.
        200: z.any(),
        404: errorSchemas.notFound,
      },
    },
    upsert: {
      method: "POST" as const,
      path: "/api/remonta/veiculos/:veiculoId/sinistro-localizacao" as const,
      input: insertRemontaSinistroLocalizacaoSchema.omit({ veiculoId: true }),
      responses: {
        200: z.any(), // { localizacao, desmontes }
        400: errorSchemas.validation,
      },
    },
  },

  inviteCodes: {
    list: {
      method: "GET" as const,
      path: "/api/invite-codes" as const,
      responses: {
        200: z.array(z.custom<typeof inviteCodes.$inferSelect>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/invite-codes" as const,
      input: z.object({
        tipo: z.enum(["VISTORIADORA", "PARCEIRO_DE_NEGOCIOS"]),
        maxUsos: z.number().int().positive().optional(),
      }),
      responses: {
        201: z.custom<typeof inviteCodes.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    validate: {
      method: "POST" as const,
      path: "/api/invite-codes/validate" as const,
      input: z.object({ code: z.string() }),
      responses: {
        200: z.object({
          valid: z.boolean(),
          tipo: z.string().optional(),
          vistoriadoraId: z.string().optional(),
        }),
        404: errorSchemas.notFound,
      },
    },
  },

  userInvites: {
    list: {
      method: "GET" as const,
      path: "/api/user-invites" as const,
      responses: {
        200: z.array(z.custom<typeof userInvites.$inferSelect>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/user-invites" as const,
      input: z.object({
        email: z.string().email().optional(),
        roles: z.array(z.string()).min(1).default(["tv"]),
        maxUsos: z.number().int().positive().optional(),
      }),
      responses: {
        201: z.custom<typeof userInvites.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    validate: {
      method: "POST" as const,
      path: "/api/user-invites/validate" as const,
      input: z.object({ code: z.string().min(1) }),
      responses: {
        200: z.object({
          valid: z.boolean(),
          roles: z.array(z.string()).optional(),
          empresaId: z.string().optional(),
          email: z.string().optional(),
        }),
        404: errorSchemas.notFound,
      },
    },
    sendEmail: {
      method: "POST" as const,
      path: "/api/user-invites/:id/send-email" as const,
      input: z.object({ email: z.string().email() }),
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
        501: errorSchemas.validation,
      },
    },
  },

  // -------------------------------------------------------------------------
  // ReMonta domain routes
  // -------------------------------------------------------------------------

  remontaPermissaoVistoriadora: {
    listSolicitantesAutorizados: {
      method: "GET" as const,
      path: "/api/remonta/vistoriadora/solicitantes-autorizados" as const,
      responses: {
        200: z.array(z.custom<typeof empresas.$inferSelect>()),
        403: errorSchemas.validation,
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/remonta/solicitante/permissao-vistoriadora" as const,
      responses: {
        200: z.custom<typeof remontaSolicitantePermissoes.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/remonta/solicitante/permissao-vistoriadora" as const,
      input: z.object({ podeCriarDossie: z.boolean() }),
      responses: {
        200: z.custom<typeof remontaSolicitantePermissoes.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
  },

  remontaVistoriadoraRts: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/vistoriadora/rts" as const,
      responses: {
        200: z.array(z.object({ id: z.string().uuid(), nome: z.string(), crea: z.string() })),
        403: errorSchemas.validation,
      },
    },
  },

  remontaDossies: {
    archive: {
      method: "POST" as const,
      path: "/api/remonta/dossies/:id/arquivar" as const,
      responses: { 200: z.custom<typeof remontaDossiesSinistro.$inferSelect>(), 403: errorSchemas.validation, 404: errorSchemas.notFound, 409: errorSchemas.validation },
    },
    reopen: {
      method: "POST" as const,
      path: "/api/remonta/dossies/:id/reabrir" as const,
      responses: { 200: z.custom<typeof remontaDossiesSinistro.$inferSelect>(), 403: errorSchemas.validation, 404: errorSchemas.notFound, 409: errorSchemas.validation },
    },
    list: {
      method: "GET" as const,
      path: "/api/remonta/dossies" as const,
      responses: {
        200: z.array(z.custom<typeof remontaDossiesSinistro.$inferSelect>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/dossies" as const,
      input: dossierIntakeSchema,
      responses: {
        201: z.custom<typeof remontaDossiesSinistro.$inferSelect>(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/remonta/dossies/:id" as const,
      responses: {
        200: z.custom<typeof remontaDossiesSinistro.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    update: {
      method: "PATCH" as const,
      path: "/api/remonta/dossies/:id" as const,
      input: z
        .object({
          status: dossieStatusSchema.optional(),
          motivoPendencia: z.string().trim().min(1).nullable().optional(),
          inspecaoAgendadaEm: z.string().datetime().nullable().optional(),
          localInspecao: z.string().trim().min(1).nullable().optional(),
        })
        .superRefine((input, context) => {
          if (!Object.values(input).some((value) => value !== undefined)) {
            context.addIssue({ code: "custom", message: "Informe ao menos uma alteracao" });
          }
          if (input.motivoPendencia !== undefined && input.status === undefined) {
            context.addIssue({
              code: "custom",
              message: "Motivo exige alteracao de status",
              path: ["motivoPendencia"],
            });
          }
        }),
      responses: {
        200: z.custom<typeof remontaDossiesSinistro.$inferSelect>(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
    createUploadIntent: {
      method: "POST" as const,
      path: "/api/remonta/dossies/:id/arquivos/upload-intent" as const,
      input: z
        .object({
          tipo: dossieArquivoTipoSchema,
          extensao: z.enum(["jpg", "jpeg", "png", "webp", "pdf"]),
        })
        .refine(
          ({ tipo, extensao }) =>
            tipo === "foto_triagem" ? extensao !== "pdf" : extensao === "pdf",
          { message: "Extensao incompativel com o tipo de arquivo", path: ["extensao"] },
        ),
      responses: {
        200: z.object({
          storagePath: z.string(),
          signedUrl: z.string().url(),
          token: z.string(),
        }),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
    listArquivos: {
      method: "GET" as const,
      path: "/api/remonta/dossies/:id/arquivos" as const,
      responses: {
        200: z.array(z.custom<typeof remontaDossieArquivos.$inferSelect>()),
        404: errorSchemas.notFound,
      },
    },
    createArquivo: {
      method: "POST" as const,
      path: "/api/remonta/dossies/:id/arquivos" as const,
      input: z
        .object({
          tipo: dossieArquivoTipoSchema,
          storagePath: z.string().min(1),
          metadata: z.record(z.unknown()).optional(),
        })
        .strict(),
      responses: {
        201: z.custom<typeof remontaDossieArquivos.$inferSelect>(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
    deleteArquivo: {
      method: "DELETE" as const,
      path: "/api/remonta/dossies/:id/arquivos/:arquivoId" as const,
      responses: {
        200: z.object({ ok: z.boolean() }),
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
        500: errorSchemas.internal,
        503: errorSchemas.internal,
      },
    },
    createLaudo: {
      method: "POST" as const,
      path: "/api/remonta/dossies/:id/laudos" as const,
      input: z.object({
        tipo: z.literal("reclassificacao"),
        ordemServicoId: z.string().uuid().optional(),
        rtId: z.string().uuid(),
        anexoAplicavel: anexoSchema,
      }),
      responses: {
        201: z.custom<typeof remontaLaudos.$inferSelect>(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
        422: errorSchemas.validation,
      },
    },
  },

  remontaOrdens: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/dossies/:id/ordens-servico" as const,
      responses: {
        200: z.array(z.custom<typeof remontaOrdensServico.$inferSelect>()),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/dossies/:id/ordens-servico" as const,
      input: z.object({ tipo: remontaServiceTypeSchema }),
      responses: {
        201: z.custom<typeof remontaOrdensServico.$inferSelect>(),
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/remonta/ordens-servico/:id" as const,
      responses: {
        200: z.custom<typeof remontaOrdensServico.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
  },

  remontaReclassificacao: {
    emit: {
      method: "POST" as const,
      path: "/api/remonta/laudos/:id/emitir" as const,
      input: z.object({}).strict(),
      responses: {
        201: z.custom<typeof remontaLaudos.$inferSelect>(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
        422: errorSchemas.validation,
        503: errorSchemas.validation,
      },
    },
    createRevision: {
      method: "POST" as const,
      path: "/api/remonta/laudos/:id/revisoes" as const,
      input: z.object({ rtId: z.string().uuid() }),
      responses: {
        201: z.custom<typeof remontaLaudos.$inferSelect>(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
        422: errorSchemas.validation,
      },
    },
  },

  remontaLaudos: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/laudos" as const,
      responses: {
        200: z.array(z.custom<typeof remontaLaudos.$inferSelect>()),
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/remonta/laudos/:id" as const,
      responses: {
        200: z.custom<typeof remontaLaudos.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/laudos" as const,
      input: insertRemontaLaudoSchema,
      responses: {
        201: z.custom<typeof remontaLaudos.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/remonta/laudos/:id" as const,
      input: insertRemontaLaudoSchema.partial(),
      responses: {
        200: z.custom<typeof remontaLaudos.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/remonta/laudos/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    updateStatus: {
      method: "PATCH" as const,
      path: "/api/remonta/laudos/:id/status" as const,
      input: z.object({ status: z.string() }),
      responses: {
        200: z.custom<typeof remontaLaudos.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
    finalize: {
      method: "PATCH" as const,
      path: "/api/remonta/laudos/:id/finalize" as const,
      input: z.object({
        conclusao: z.enum(["recuperavel", "irrecuperavel"]).optional(),
        justificativa: z.string().min(20).optional(),
        classificacaoMonta: z.unknown().optional(),
      }),
      responses: {
        200: z.custom<typeof remontaLaudos.$inferSelect>(),
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
    updateNexoCausal: {
      method: "PATCH" as const,
      path: "/api/remonta/laudos/:id/nexo-causal" as const,
      input: z.object({
        nexoNarrativa: z.string().optional(),
        nexoDinamica: z.string().optional(),
        nexoGeometria: z.string().optional(),
        nexoIndicativos: z.string().optional(),
        nexoClassificacao: z
          .enum([
            "confirmado",
            "provavel",
            "parcial",
            "inconclusivo",
            "nao_demonstrado",
            "incompativel",
          ])
          .optional(),
      }),
      responses: {
        200: z.custom<typeof remontaLaudos.$inferSelect>(),
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    decideAuditoria: {
      method: "POST" as const,
      path: "/api/remonta/laudos/:id/auditoria" as const,
      input: z.object({
        decisao: z.enum(["aprovado", "devolvido"]),
        justificativa: z.string().min(10),
      }),
      responses: {
        200: z.custom<typeof remontaLaudos.$inferSelect>(),
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
  },

  remontaOrcamentosItem: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/dossies/:dossieId/orcamento" as const,
      responses: {
        200: z.array(z.custom<typeof remontaOrcamentosItem.$inferSelect>()),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/dossies/:dossieId/orcamento" as const,
      input: z.object({
        categoria: z.enum([
          "peca", "mao_de_obra", "funilaria", "pintura", "mecanica",
          "eletrica", "diagnostico", "calibracao", "alinhamento",
          "transporte", "terceiros", "tributos", "nao_coberto",
        ]),
        descricao: z.string().min(3),
        valorUnitario: moneySchema,
        quantidade: decimalSchema.refine((v) => Number(v) > 0 && Number(v) <= 99999999.99, "Quantidade deve ser positiva e menor que 100 milhões").default("1"),
        ordemServicoId: z.string().uuid().optional(),
        justificativa: z.string().optional(),
      }),
      responses: {
        201: z.custom<typeof remontaOrcamentosItem.$inferSelect>(),
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    decide: {
      method: "PATCH" as const,
      path: "/api/remonta/orcamento-itens/:id/decisao" as const,
      input: z.object({
        decisao: z.enum(["aprovado", "aprovado_com_ajuste", "devolvido", "reprovado"]),
        valorAprovado: moneySchema.optional(),
        motivoDecisao: z.string().min(10),
      }),
      responses: {
        200: z.custom<typeof remontaOrcamentosItem.$inferSelect>(),
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
  },

  remontaOcorrencias: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/dossies/:dossieId/ocorrencias" as const,
      responses: {
        200: z.array(z.custom<typeof remontaOcorrencias.$inferSelect & { autorNome: string | null }>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/dossies/:dossieId/ocorrencias" as const,
      input: z.object({
        tipo: z.enum(["divergencia", "correcao", "observacao"]),
        descricao: z.string().min(3),
        laudoId: z.string().uuid().optional(),
        fotoUrl: z.string().url().optional(),
        referenciaOcorrenciaId: z.string().uuid().optional(),
      }),
      responses: {
        201: z.custom<typeof remontaOcorrencias.$inferSelect>(),
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },

  remontaCategorias: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/categorias" as const,
      responses: {
        200: z.array(
          z.custom<typeof remontaCategoriasEstruturais.$inferSelect>(),
        ),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/categorias" as const,
      input: insertRemontaCategoriaEstrutural,
      responses: {
        201: z.custom<typeof remontaCategoriasEstruturais.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
  },

  remontaElementos: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/elementos" as const,
      responses: {
        200: z.array(
          z.custom<typeof remontaElementosEstruturais.$inferSelect>(),
        ),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/elementos" as const,
      input: insertRemontaElementoEstrutural,
      responses: {
        201: z.custom<typeof remontaElementosEstruturais.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/remonta/elementos/:id" as const,
      input: z.object({
        nome: z.string().min(1).max(100).optional(),
        descricao: z.string().max(300).optional(),
      }),
      responses: {
        200: z.custom<typeof remontaElementosEstruturais.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
  },

  remontaItensLaudo: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/laudos/:laudoId/itens" as const,
      responses: {
        200: z.array(z.custom<typeof remontaItensLaudo.$inferSelect>()),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
    upsert: {
      method: "PUT" as const,
      path: "/api/remonta/laudos/:laudoId/itens/:elementoId" as const,
      input: z.object({
        status: z.enum(["sim", "nao", "na"]),
        observacao: z.string().nullable().optional(),
      }).strict(),
      responses: {
        200: z.custom<typeof remontaItensLaudo.$inferSelect>(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
  },

  remontaFotos: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/laudos/:laudoId/fotos" as const,
      responses: { 200: z.array(z.any()), 404: errorSchemas.notFound },
    },
    createUploadIntent: {
      method: "POST" as const,
      path: "/api/remonta/laudos/:laudoId/fotos/upload-intent" as const,
      input: z.object({ extensao: z.enum(["jpg", "jpeg", "png", "webp"]) }),
      responses: {
        200: z.object({
          storagePath: z.string(),
          signedUrl: z.string().url(),
          token: z.string(),
        }),
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/laudos/:laudoId/fotos" as const,
      input: z
        .object({
          storagePath: z.string().min(1),
          elementoId: z.string().uuid().optional(),
          tipoFoto: z.enum(remontaTiposFoto),
        })
        .strict(),
      responses: {
        201: z.any(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/remonta/fotos/:id" as const,
      responses: {
        200: z.object({ ok: z.boolean() }),
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
        500: errorSchemas.internal,
        503: errorSchemas.internal,
      },
    },
  },

  remontaReparos: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/laudos/:laudoId/reparos" as const,
      responses: { 200: z.array(z.any()) },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/laudos/:laudoId/reparos" as const,
      input: z.object({
        descricao: z.string().min(3),
        numeroNf: z.string().min(1),
        valor: z.string().min(1),
        fornecedor: z.string().min(2),
      }),
      responses: { 201: z.any(), 400: errorSchemas.validation },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/remonta/reparos/:id" as const,
      responses: { 200: z.object({ ok: z.boolean() }), 404: errorSchemas.notFound },
    },
  },

  remontaArts: {
    list: {
      method: "GET" as const,
      path: "/api/remonta/arts" as const,
      responses: { 200: z.array(z.any()) },
    },
    create: {
      method: "POST" as const,
      path: "/api/remonta/arts" as const,
      input: z.object({
        rtId: z.string().uuid(),
        numeroArt: z.string().trim().min(1, 'Informe o número da ART'),
        descricao: z.string().optional(),
        // string vazia (campo deixado em branco no form) equivale a
        // ausente — sem isso, "" chega inteiro no INSERT numeric/date e
        // quebra com "invalid input syntax for type numeric/date"
        valor: emptyStringToUndefined.pipe(z.string()
          .regex(/^-?\d+(?:\.\d{1,2})?$/, 'Informe um valor numérico com até duas casas decimais')
          .refine(value => Math.abs(Number(value)) <= 99_999_999.99, 'Valor excede o limite permitido')
          .optional()),
        dataInicio: isoDateSchema,
        dataFim: emptyStringToUndefined.pipe(isoDateSchema.optional()),
        status: z.enum(["ativa", "encerrada"]).default("ativa"),
      }).refine(value => !value.dataFim || value.dataFim >= value.dataInicio, {
        message: 'A data final não pode ser anterior à data inicial', path: ['dataFim'],
      }),
      responses: { 201: z.any(), 400: z.object({ message: z.string() }) },
    },
  },

  auditoria: {
    list: {
      method: "GET" as const,
      path: "/api/auditoria" as const,
      responses: {
        200: z.object({
          data: z.array(z.any()),
          total: z.number(),
          page: z.number(),
          limit: z.number(),
        }),
      },
    },
  },


  notificacoes: {
    list: {
      method: "GET" as const,
      path: "/api/notificacoes" as const,
      responses: { 200: z.array(z.any()) },
    },
    marcarLida: {
      method: "PATCH" as const,
      path: "/api/notificacoes/:id/lida" as const,
      responses: { 200: z.any(), 404: errorSchemas.notFound },
    },
    marcarTodasLidas: {
      method: "POST" as const,
      path: "/api/notificacoes/todas-lidas" as const,
      responses: { 200: z.object({ message: z.string() }) },
    },
  },

  apks: {
    list: {
      method: "GET" as const,
      path: "/api/owner/apks" as const,
      responses: {
        200: z.array(z.custom<typeof remontaApks.$inferSelect>()),
      },
    },
    upload: {
      method: "POST" as const,
      path: "/api/owner/apks" as const,
      responses: {
        201: z.custom<typeof remontaApks.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    setPrincipal: {
      method: "PUT" as const,
      path: "/api/owner/apks/:id/principal" as const,
      responses: {
        200: z.custom<typeof remontaApks.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/owner/apks/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    download: {
      method: "GET" as const,
      path: "/api/apks/download" as const,
      responses: {
        200: z.object({ url: z.string(), versao: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
};

export function buildUrl(
  path: string,
  params?: Record<string, string | number>,
): string {
  let url = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (url.includes(`:${key}`)) {
        url = url.replace(`:${key}`, String(value));
      }
    });
  }
  return url;
}

export const budgetResubmissionInput = api.remontaOrcamentosItem.create.input.omit({ ordemServicoId: true }).extend({ decididoEm: z.string().datetime() }).strict();
